import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/components/theme/theme-provider';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Login } from '@/components/auth/Login';
import { useAppSelector } from '@/app/hooks';
import { selectIsAuthenticated } from '@/features/auth/authSlice';
import { useToast } from '@/hooks/use-toast';
import HomeImageSlider from '@/components/home/HomeImageSlider';
// The logo should be placed in src/assets/images/
import Logo from '@/assets/images/tulasi-circle.png';
import LogoWhite from '@/assets/images/tulasi-circle_white.png';

const hasLoggedInBefore = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.localStorage.getItem('tulasi.hasLoggedInBefore') === 'true';
  } catch {
    return false;
  }
};

type HomeLocationState = {
  authModal?: boolean;
  authInitialMode?: 'login' | 'register';
  redirectTo?: string;
};

const HomePage = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authRedirectTo, setAuthRedirectTo] = useState<string | null>(null);
  const homeLocationState = location.state as HomeLocationState | null;
  const defaultAuthInitialMode = useMemo(
    () => homeLocationState?.authInitialMode ?? (hasLoggedInBefore() ? 'login' : 'register'),
    [homeLocationState?.authInitialMode],
  );
  const [authInitialMode, setAuthInitialMode] = useState<'login' | 'register'>(defaultAuthInitialMode);

  useEffect(() => {
    if (!homeLocationState?.authModal) {
      return;
    }

    setAuthRedirectTo(homeLocationState.redirectTo ?? null);
    setAuthInitialMode(homeLocationState.authInitialMode ?? defaultAuthInitialMode);
    setIsAuthModalOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [
    defaultAuthInitialMode,
    homeLocationState?.authInitialMode,
    homeLocationState?.authModal,
    homeLocationState?.redirectTo,
    location.pathname,
    navigate,
  ]);

  const handleRegistrationClick = () => {
    if (isAuthenticated) {
      toast({
        title: t('auth.alreadyLoggedIn', 'You are already logged in.'),
      });
      return;
    }
    setAuthInitialMode(defaultAuthInitialMode);
    setAuthRedirectTo(null);
    setIsAuthModalOpen(true);
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-start bg-brand-background px-4 pb-8 pt-6 text-brand-brown dark:bg-gray-900 dark:text-gray-300 sm:pt-8">
      <div className="flex w-full max-w-4xl flex-col items-center">
        {/* Header Section */}
        <header className="w-full text-center">
          <img src={theme === 'dark' ? LogoWhite : Logo} alt="Tulasi Home Logo" className="mx-auto mb-4 h-40 w-40 object-contain sm:h-52 sm:w-52 md:h-60 md:w-60" />
          <h1 className="text-4xl font-bold tracking-[0.18em] text-brand-green dark:text-gray-100 sm:text-5xl">
            {t('homepage.brandTitle', 'TULASI HOME')}
          </h1>
          <p className="mt-2 text-xs tracking-[0.18em] text-brand-green dark:text-gray-300 sm:text-sm">
            {t('homepage.brandTagline', 'PRINT & ARTISAN PRODUCTION STUDIO')}
          </p>
          <div className="my-6 w-40 mx-auto border-t border-brand-green/50 dark:border-teal-300/50"></div>
          <p className="mx-auto max-w-xl text-base italic text-brand-brown dark:text-gray-400">
            {t('homepage.brandSubtitle', 'From sacred inspiration to tangible art.')}
          </p>
        </header>

        {/* Main Content */}
        <main className="mt-10 w-full text-center sm:mt-12">
          <h2 className="mx-auto mb-8 max-w-3xl text-3xl font-semibold leading-tight text-brand-brown dark:text-gray-100 sm:text-4xl">
            {t('welcome', 'Ласкаво просимо')}
          </h2>
          <div className="mx-auto flex w-full max-w-xs flex-col items-stretch gap-4 sm:max-w-none sm:flex-row sm:justify-center sm:items-center sm:gap-4">
            <Button
              variant="outline"
              className="w-full border-brand-tan px-8 py-2 text-brand-brown transition-colors duration-300 hover:bg-brand-tan hover:text-white dark:border-amber-800 dark:text-gray-300 dark:hover:bg-amber-800 dark:hover:text-white sm:w-auto"
              onClick={handleRegistrationClick}
            >
              {t('auth.register', 'Реєстрація')}
            </Button>
            <Button
              asChild
              className="w-full bg-brand-tan px-8 py-2 text-white transition-colors duration-300 hover:bg-opacity-90 dark:bg-amber-700 dark:hover:bg-amber-600 sm:w-auto"
            >
              <Link to="/studio">{t('cta.get_started', 'Get started')}</Link>
            </Button>
            <Button
              variant="outline"
              className="w-full border-brand-tan px-8 py-2 text-brand-brown transition-colors duration-300 hover:bg-brand-tan hover:text-white dark:border-amber-800 dark:text-gray-300 dark:hover:bg-amber-800 dark:hover:text-white sm:w-auto"
              onClick={() => navigate('/about')}
            >
              {t('cta.learn_more', 'Дізнатися більше')}
            </Button>
          </div>
        </main>

        <HomeImageSlider />
      </div>

      {/* Footer Text */}
      <footer className="mt-auto pt-10 text-center">
        <p className="tracking-widest text-sm text-brand-brown/80 dark:text-gray-500">
          {t('footer.art_print_tagline', 'Art print on paper and canvas.')}
        </p>
      </footer>

      <Dialog open={isAuthModalOpen} onOpenChange={setIsAuthModalOpen}>
        <DialogContent className="max-w-lg border border-white/10 bg-background p-6 shadow-2xl">
          <DialogHeader className="sr-only">
            <DialogTitle>{t('auth.register', 'Registration')}</DialogTitle>
            <DialogDescription>
              {t('homepage.authDialogDescription', 'Sign up or log in to continue.')}
            </DialogDescription>
          </DialogHeader>
          <Login
            standalone
            appearance="light"
            initialMode={authInitialMode}
            onSuccess={() => {
              setIsAuthModalOpen(false);
              if (authRedirectTo) {
                navigate(authRedirectTo);
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePage;
